import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Stack, Typography } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import { useTranslation } from "react-i18next";

export default function NotFound() {
  const navigate = useNavigate();
  const { t } = useTranslation("pages-products");

  // nginx returns the SPA shell for client-side routes, so an unknown URL is
  // initially an HTTP 200. Keep this route out of search indexes without
  // leaving a noindex directive behind after in-app navigation.
  useEffect(() => {
    const existing = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const previousContent = existing?.getAttribute("content");
    const robots = existing ?? document.createElement("meta");

    robots.name = "robots";
    robots.setAttribute("content", "noindex");
    if (!existing) document.head.appendChild(robots);

    return () => {
      if (!existing) {
        robots.remove();
      } else if (previousContent == null) {
        robots.removeAttribute("content");
      } else {
        robots.setAttribute("content", previousContent);
      }
    };
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        textAlign: "center",
        gap: 2,
      }}
    >
      {/* Forge icon + 404 */}
      <Stack alignItems="center" spacing={1}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/favicon_1.png`}
          alt={t("NotFound.logoAlt")}
          sx={{ width: 64, height: 64, objectFit: "contain", opacity: 0.5 }}
        />
        <Typography
          variant="h1"
          sx={{ fontSize: "5rem", fontWeight: 500, color: "divider", lineHeight: 1 }}
        >
          404
        </Typography>
      </Stack>

      <Typography variant="h5" fontWeight={500}>
        {t("NotFound.title")}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 380 }}>
        {t("NotFound.description")}
      </Typography>
      <Button
        variant="contained"
        startIcon={<HomeIcon />}
        onClick={() => navigate("/")}
        sx={{ mt: 1 }}
      >
        {t("NotFound.backToHomeButton")}
      </Button>
    </Box>
  );
}
